'use client';

import { useState } from 'react';
import { CalculatorShell } from 'components/Widget/shared/CalculatorShell';
import { formatAmount } from 'components/Widget/shared/currency';
import { useCurrencyFields } from 'components/Widget/shared/useCurrencyFields';
import { useNumberFields } from 'components/Widget/shared/useNumberFields';
import { useResultScroll } from 'components/Widget/shared/useResultScroll';
import { withPlaceholders } from 'components/Widget/shared/types';
import { calculatePaycheck } from './paycheck';
import { PaycheckCalculatorForm } from './PaycheckCalculatorForm';
import { PaycheckCalculatorResult } from './PaycheckCalculatorResult';
import { PaycheckCalculatorSummary } from './PaycheckCalculatorSummary';
import {
  ANNUAL_SALARY_FIELD,
  DEFAULT_PAY_TYPE,
  DISCLAIMER,
  HOURLY_WAGE_FIELD,
  INITIAL_CURRENCY_VALUES,
  INITIAL_HOURS_VALUES,
  NO_EXEMPTIONS,
  TITLE,
  WEEKLY_HOURS_FIELD,
  WEEKLY_HOURS_FRACTION_DIGITS,
} from './constants';
import { validatePaycheckForm } from './validation';
import type { ExemptionKey, PaycheckCalculatorState, PaycheckExemptions, PayFrequency, PayType } from './types';

const INITIAL_STATE: PaycheckCalculatorState = { errors: {}, panel: null };

export const PaycheckCalculator = () => {
  const [state, setState] = useState<PaycheckCalculatorState>(INITIAL_STATE);
  const [payFrequency, setPayFrequency] = useState<PayFrequency | null>(null);
  const [payType, setPayType] = useState<PayType>(DEFAULT_PAY_TYPE);
  const [exemptions, setExemptions] = useState<PaycheckExemptions>(NO_EXEMPTIONS);
  // The money fields already show their own `$` affix, so blur must not format a second one in.
  const currency = useCurrencyFields(INITIAL_CURRENCY_VALUES, formatAmount);
  const hours = useNumberFields(INITIAL_HOURS_VALUES, WEEKLY_HOURS_FRACTION_DIGITS);
  const { resultsRef, scrollToResults } = useResultScroll();

  const handleCalculate = () => {
    // An untouched field calculates on the greyed default the user can see, rather than failing validation.
    const currencyValues = withPlaceholders(
      currency.normalize(),
      [HOURLY_WAGE_FIELD, ANNUAL_SALARY_FIELD],
      currency.isTouched,
    );
    const hoursValues = withPlaceholders(hours.normalize(), [WEEKLY_HOURS_FIELD], hours.isTouched);
    const hourlyWage = currencyValues[HOURLY_WAGE_FIELD.label];
    const weeklyHours = hoursValues[WEEKLY_HOURS_FIELD.label];
    const annualSalary = currencyValues[ANNUAL_SALARY_FIELD.label];
    const errors = validatePaycheckForm({ payFrequency, payType, hourlyWage, weeklyHours, annualSalary });

    // The form stays mounted beside the results, so a field still being edited keeps its unformatted digits.
    currency.commit();
    hours.commit();

    // Checked on the frequency as well as the errors so the narrowing reaches `calculatePaycheck`.
    if (Object.keys(errors).length > 0 || payFrequency === null) {
      // A failed Calculate raises the field errors and leaves any result already on screen alone.
      setState(previous => ({ ...previous, errors }));

      return;
    }

    const result = calculatePaycheck({
      payFrequency,
      pay: payType === 'hourly' ? { payType, hourlyWage, weeklyHours } : { payType, annualSalary },
      filingStatus: 'single',
      exemptions,
    });
    setState({ errors: {}, panel: { result, payFrequency } });
    scrollToResults();
  };

  const handleReset = () => {
    setState(INITIAL_STATE);
    setPayFrequency(null);
    setPayType(DEFAULT_PAY_TYPE);
    setExemptions(NO_EXEMPTIONS);
    currency.reset();
    hours.reset();
  };

  // Any selection is valid, so this error clears on the spot; typed fields' errors wait for Calculate.
  const handlePayFrequencyChange = (key: PayFrequency) => {
    setPayFrequency(key);
    setState(previous => ({ ...previous, errors: { ...previous.errors, payFrequency: undefined } }));
  };

  const handleExemptionChange = (key: ExemptionKey, isExempt: boolean) => {
    setExemptions(previous => ({ ...previous, [key]: isExempt }));
  };

  return (
    <CalculatorShell
      title={TITLE}
      disclaimer={DISCLAIMER}
      collapsibleDisclaimer
      summary={state.panel && <PaycheckCalculatorSummary panel={state.panel} />}
      resultsRef={resultsRef}
      // `null` rather than nothing: it reserves the 4fr column, so the layout does not shift when the
      // first calculation renders a panel here.
      results={state.panel && <PaycheckCalculatorResult panel={state.panel} />}>
      <PaycheckCalculatorForm
        errors={state.errors}
        currencyFieldProps={currency.fieldProps}
        hoursFieldProps={hours.fieldProps}
        payFrequency={payFrequency}
        payType={payType}
        exemptions={exemptions}
        onPayFrequencyChange={handlePayFrequencyChange}
        onPayTypeChange={setPayType}
        onExemptionChange={handleExemptionChange}
        onCalculate={handleCalculate}
        onReset={handleReset}
      />
    </CalculatorShell>
  );
};
